import { useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { toast } from "react-toastify";
import { RotatingLines } from "react-loader-spinner";
import {
  adminYarpaUsageReportCounts,
  adminYarpaUsageReportExport,
} from "../../../config/service";

const DATE_FORMAT = "YYYY-MM-DD";
const MAX_RANGE_DAYS = 366;

const lastMonthRange = () => ({
  startDate: moment().subtract(1, "month").startOf("month").toDate(),
  endDate: moment().subtract(1, "month").endOf("month").toDate(),
});

const filenameFromResponse = (response) => {
  const disposition = response?.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename="?([^";]+)"?/);
  return match
    ? match[1]
    : `yarpa-usage-report-${moment().format(DATE_FORMAT)}.csv`;
};

const errorMessageFrom = (error, fallback) => {
  const data = error?.response?.data;
  if (data instanceof ArrayBuffer) {
    try {
      return JSON.parse(new TextDecoder().decode(data))?.message || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.message || error?.message || fallback;
};

/**
 * Historical YARPA prescription usage for Admin → Reports.
 * Backend must count actual YARPA sends in the range — not current
 * doctor.prescriptionProvider.
 */
const YarpaUsageReport = () => {
  const initialRange = lastMonthRange();
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const rangeDays = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return (
      moment(endDate).startOf("day").diff(moment(startDate).startOf("day"), "days") +
      1
    );
  }, [startDate, endDate]);

  const rangeError = useMemo(() => {
    if (!startDate || !endDate) return "Choose a start and end date.";
    if (rangeDays <= 0) return "The start date is after the end date.";
    if (rangeDays > MAX_RANGE_DAYS) {
      return "The range is longer than a year. Narrow it down.";
    }
    return "";
  }, [startDate, endDate, rangeDays]);

  const buildPayload = () => ({
    startDay: moment(startDate).format(DATE_FORMAT),
    endDay: moment(endDate).format(DATE_FORMAT),
    // Private Hidoc YARPA usage only — exclude RX / PrescriptionOnly for now.
    scope: "hidoc",
  });

  const handlePreview = async () => {
    if (rangeError) return toast.error(rangeError);

    setIsLoading(true);
    try {
      const response = await adminYarpaUsageReportCounts(buildPayload());
      setSummary(response?.data?.summary || null);
    } catch (error) {
      setSummary(null);
      toast.error(
        errorMessageFrom(
          error,
          "Could not load YARPA usage counts. Backend report endpoint may be missing.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    if (rangeError) return toast.error(rangeError);

    setIsLoading(true);
    try {
      const response = await adminYarpaUsageReportExport(buildPayload());
      const url = URL.createObjectURL(
        new Blob([response.data], { type: "text/csv" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filenameFromResponse(response));
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(
        errorMessageFrom(
          error,
          "Could not export YARPA usage report. Backend report endpoint may be missing.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const doctors = Array.isArray(summary?.doctors) ? summary.doctors : [];

  return (
    <div className="card mb-4">
      <div className="card-body p-4">
        <h5 className="mb-1">Yarpa Monthly Usage</h5>
        <p className="text-muted mb-4">
          Private Hidoc doctors who actually sent prescriptions via YARPA in
          the selected range (historical usage, not current provider setting).
          RX / PrescriptionOnly is excluded for now.
        </p>

        <div className="row">
          <div className="col-md-4 mb-3">
            <label className="form-label fw-bold">From</label>
            <DatePicker
              selected={startDate}
              onChange={(date) => {
                setStartDate(date);
                setSummary(null);
              }}
              dateFormat="dd/MM/yyyy"
              className="form-control"
            />
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label fw-bold">To</label>
            <DatePicker
              selected={endDate}
              onChange={(date) => {
                setEndDate(date);
                setSummary(null);
              }}
              dateFormat="dd/MM/yyyy"
              className="form-control"
            />
          </div>
        </div>

        {rangeError ? (
          <p className="text-danger mb-4">{rangeError}</p>
        ) : (
          <p className="text-muted mb-4">
            {rangeDays} days — typically set to one calendar month
          </p>
        )}

        <div className="d-flex flex-wrap align-items-center gap-2 pt-2">
          <Button
            variant="outline-dark"
            disabled={isLoading || Boolean(rangeError)}
            onClick={handlePreview}
          >
            Preview counts
          </Button>
          <Button
            className="download_report"
            disabled={isLoading || Boolean(rangeError)}
            onClick={handleExport}
          >
            Download CSV
          </Button>
          {isLoading && <RotatingLines width="28" strokeColor="grey" />}
        </div>

        {summary && (
          <div className="mt-4">
            <h5 className="mb-3">
              Distinct doctors who used YARPA:{" "}
              {summary.distinctDoctorCount ?? doctors.length}
              {typeof summary.prescriptionInstanceCount === "number" && (
                <span className="text-muted fs-6">
                  {" "}
                  ({summary.prescriptionInstanceCount} YARPA prescription
                  instances)
                </span>
              )}
            </h5>

            {doctors.length > 0 && (
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Doctor</th>
                      <th>Doctor ID</th>
                      <th>Meeting IDs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doctors.map((doctor) => {
                      const meetingIds = Array.isArray(doctor.meetingIds)
                        ? doctor.meetingIds
                        : [];
                      return (
                        <tr
                          key={
                            doctor.doctorId ||
                            doctor.doctorUserId ||
                            doctor.licenceNumber ||
                            doctor.doctorName
                          }
                        >
                          <td>
                            {doctor.doctorName ||
                              [doctor.first_name, doctor.last_name]
                                .filter(Boolean)
                                .join(" ") ||
                              "—"}
                          </td>
                          <td>
                            {doctor.doctorId ||
                              doctor.doctorUserId ||
                              doctor.id ||
                              "—"}
                          </td>
                          <td className="text-break">
                            {meetingIds.length > 0
                              ? meetingIds.join(", ")
                              : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default YarpaUsageReport;
